import {
	GeoJSON,
	Geometry, 
	LineString, 
	MultiLineString, 
	MultiPoint, 
	Point, 
	Position 
} from "geojson";
import { isValueNullish } from "../../../utils/valueUtilities";
import {
	allowsEndpoint,
	getBoundaryOperator,
	includesBoundary,
	includesEnvelopeBoundary,
	nextDown,
	nextUp,
	type BoundaryOperator
} from "./boundary.ts";
import {
	geometryContainsPoint,
	geometryCoversPoint,
	getEnvelope,
	readGeometries,
	toExteriorPolygon
} from "./geometries.ts";
import booleanPointOnLine from "@turf/boolean-point-on-line";

export const CONTAINS_SCOPES = {
	CoordXY: "COORD_XY",
	CoordX: "COORD_X",
	CoordY: "COORD_Y",
	EvaluationSet: "EVALUATIONSET",
	Type: "TYPE"
} as const;

export type ContainsScope = typeof CONTAINS_SCOPES[keyof typeof CONTAINS_SCOPES];

export interface ContainsCondition {
	Field?: { Id?: number };
	Scope?: { Code?: string };
	Operator?: { Code?: string };
	Value?: unknown;
}

export interface EntityGeometry {
	Type?: { Code?: string };
	Definition?: unknown;
	definition?: unknown;
}

export interface ContainsFieldValue {
	FieldId?: number;
	Longitude?: number | null;
	Latitude?: number | null;
	Number?: number | null;
	EntityId?: number | null;
	DataId?: number | null;
	Geometries?: EntityGeometry[];
	Shape?: { GeoJson?: string };
}

export interface ContainsMetadata {
	rule?: { Conditions?: ContainsCondition[] };
}

export interface EvaluationPoint {
	coordX: number;
	coordY: number;
	coordXOperator: BoundaryOperator;
	coordYOperator: BoundaryOperator;
}

export type FieldValuesById = Record<string | number, ContainsFieldValue[] | undefined>;
export type ValuesByContextId = Record<string, FieldValuesById>;
type ConditionByScope = Partial<Record<string, ContainsCondition>>;

const tryGetCoordinates = (
	conditionByScope: ConditionByScope,
	fieldValuesById: FieldValuesById
): EvaluationPoint | undefined => {
	const coordXYCondition = conditionByScope[CONTAINS_SCOPES.CoordXY];
	if(coordXYCondition?.Field?.Id !== undefined) {
		const coordinate = (fieldValuesById[coordXYCondition.Field.Id] ?? [])[0];
		if(isValueNullish(coordinate?.Longitude) || isValueNullish(coordinate?.Latitude)) {
			return undefined;
		}

		const boundaryOperator = getBoundaryOperator(coordXYCondition.Operator?.Code);
		return {
			coordX: Number(coordinate.Longitude),
			coordY: Number(coordinate.Latitude),
			coordXOperator: boundaryOperator,
			coordYOperator: boundaryOperator
		};
	}

	const coordXCondition = conditionByScope[CONTAINS_SCOPES.CoordX];
	const coordYCondition = conditionByScope[CONTAINS_SCOPES.CoordY];
	if(coordXCondition?.Field?.Id === undefined || coordYCondition?.Field?.Id === undefined) {
		return undefined;
	}

	const coordXValue = (fieldValuesById[coordXCondition.Field.Id] ?? [])[0];
	const coordYValue = (fieldValuesById[coordYCondition.Field.Id] ?? [])[0];
	if(isValueNullish(coordXValue?.Number) || isValueNullish(coordYValue?.Number)) {
		return undefined;
	}

	return {
		coordX: Number(coordXValue.Number),
		coordY: Number(coordYValue.Number),
		coordXOperator: getBoundaryOperator(coordXCondition.Operator?.Code),
		coordYOperator: getBoundaryOperator(coordYCondition.Operator?.Code)
	};
};

const evaluateBoundary = (
	geometry: Geometry | undefined,
	point: Position,
	isCoordX: boolean,
	boundaryOperator: BoundaryOperator
): boolean | undefined => {
	const lowerProbe: Position = isCoordX
		? [nextDown(point[0]), point[1]]
		: [point[0], nextDown(point[1])];
	const upperProbe: Position = isCoordX
		? [nextUp(point[0]), point[1]]
		: [point[0], nextUp(point[1])];
	const lowerSideIsCovered = geometryCoversPoint(geometry, lowerProbe);
	const upperSideIsCovered = geometryCoversPoint(geometry, upperProbe);
	if(lowerSideIsCovered === upperSideIsCovered) {
		return undefined;
	}

	const coordinate = isCoordX ? point[0] : point[1];
	const includes = includesBoundary(
		boundaryOperator,
		coordinate,
		lowerSideIsCovered,
		upperSideIsCovered
	);
	return includes;
};

const evaluateEnvelopeBoundary = (
	geometry: Geometry | undefined,
	coordinate: number,
	isCoordX: boolean,
	boundaryOperator: BoundaryOperator
): boolean | undefined => {
	const envelope = getEnvelope(geometry);
	const minimum = isCoordX ? envelope.minX : envelope.minY;
	const maximum = isCoordX ? envelope.maxX : envelope.maxY;
	if(minimum === maximum) {
		return undefined;
	}
	if(coordinate === minimum) {
		const includes = includesEnvelopeBoundary(boundaryOperator, coordinate, maximum, true);
		return includes;
	}
	if(coordinate === maximum) {
		const includes = includesEnvelopeBoundary(boundaryOperator, coordinate, minimum, false);
		return includes;
	}

	return undefined;
};

const checkPolygon = (geometry: Geometry, testPoint: EvaluationPoint): boolean => {
	const point: Position = [testPoint.coordX, testPoint.coordY];
	if(geometryContainsPoint(geometry, point)) {
		return true;
	}

	if(!geometryCoversPoint(geometry, point)) {
		return false;
	}

	const xBoundary = evaluateBoundary(geometry, point, true, testPoint.coordXOperator);
	const yBoundary = evaluateBoundary(geometry, point, false, testPoint.coordYOperator);
	if(xBoundary === false || yBoundary === false) {
		return false;
	}
	if(!isValueNullish(xBoundary) || !isValueNullish(yBoundary)) {
		return true;
	}

	const xEnvelopeBoundary = evaluateEnvelopeBoundary(geometry, point[0], true, testPoint.coordXOperator);
	const yEnvelopeBoundary = evaluateEnvelopeBoundary(geometry, point[1], false, testPoint.coordYOperator);
	if(xEnvelopeBoundary === false || yEnvelopeBoundary === false) {
		return false;
	}

	return true;
};

const checkLineString = (line: LineString | MultiLineString, testPoint: EvaluationPoint): boolean => {
	const point: Position = [testPoint.coordX, testPoint.coordY];

	if(line?.type === "MultiLineString") {
		const isContained = (line.coordinates ?? []).some(component => checkLineString({ type: "LineString", coordinates: component }, testPoint));
		return isContained;
	}

	if(!line?.coordinates) {
		return false;
	}
	
	// Strictly on the line
	if(booleanPointOnLine(point, line, { ignoreEndVertices: true })) {
		return true;
	}

	// Not even on the line at all
	if(!booleanPointOnLine(point, line, { ignoreEndVertices: false })) {
		return false;
	}

	const endpointIndex = line.coordinates.findIndex(coordinate =>
		coordinate[0] === point[0] && coordinate[1] === point[1]
	);
	if(endpointIndex < 0) {
		return false;
	}

	const adjacentIndex = endpointIndex === 0 ? 1 : line.coordinates.length - 2;
	const adjacentCoordinate = line.coordinates[adjacentIndex];
	const localXIsLower = adjacentCoordinate[0] === point[0]
		? undefined
		: adjacentCoordinate[0] < point[0];
	const localYIsLower = adjacentCoordinate[1] === point[1]
		? undefined
		: adjacentCoordinate[1] < point[1];
	const allowsXEndpoint = allowsEndpoint(testPoint.coordXOperator, point[0], localXIsLower);
	const allowsYEndpoint = allowsEndpoint(testPoint.coordYOperator, point[1], localYIsLower);
	const allowsPointEndpoint = allowsXEndpoint && allowsYEndpoint;
	return allowsPointEndpoint;
};

const checkPoint = (geometryPoint: Point | MultiPoint, testPoint: EvaluationPoint): boolean => {
	if(geometryPoint?.type === "MultiPoint") {
		const isContained = (geometryPoint.coordinates ?? []).some(component => checkPoint({ type: "Point", coordinates: component }, testPoint));
		return isContained;
	}

	if(!geometryPoint?.coordinates) {
		return false;
	}

	return geometryPoint.coordinates[0] === testPoint.coordX &&
		geometryPoint.coordinates[1] === testPoint.coordY;
};

const containsGeometry = (geometry: Geometry | undefined, testPoint: EvaluationPoint): boolean => {
	if(isValueNullish(geometry) || isValueNullish(testPoint)) {
		return false;
	}

	if(geometry?.type === "GeometryCollection") {
		const isContained = (geometry.geometries ?? []).some(component => containsGeometry(component, testPoint));
		return isContained;
	}

	const type = geometry?.type;
	switch(type) {
		case "Polygon": {
			const polygonIsContained = checkPolygon(geometry, testPoint);
			return polygonIsContained;
		}

		case "MultiPolygon": {
			const multiPolygonIsContained = (geometry.coordinates ?? []).some(component => {
				const polygon: Geometry = { type: "Polygon", coordinates: component };
				return checkPolygon(polygon, testPoint);
			});
			return multiPolygonIsContained;
		}

		case "LineString":
		case "MultiLineString": {
			const lineIsContained = checkLineString(geometry, testPoint);
			return lineIsContained;
		}

		case "Point":
		case "MultiPoint": {
			const pointIsContained = checkPoint(geometry, testPoint);
			return pointIsContained;
		}

		default:
			return false;
	}
};

const isPointInShape = (
	fieldValues: ContainsFieldValue[] | undefined,
	testPoint: EvaluationPoint,
	angularServices?: any
): boolean => {
	if(!angularServices?.$base64) {
		return false;
	}

	const evaluationPolygons = (fieldValues ?? [])
		.reduce<Geometry[]>((polygons, value) => {
			const encoded = value?.Shape?.GeoJson;
			if(!encoded) {
				return polygons;
			}

			const geometries = readGeometries(angularServices.$base64.decode(encoded))
				.reduce<Geometry[]>((matched, geometry) => {
					const polygon = toExteriorPolygon(geometry);
					if(polygon) {
						matched.push(polygon);
					}

					return matched;
				}, polygons);
			return geometries;
		}, []);

	const isContained = evaluationPolygons.some(polygon => containsGeometry(polygon, testPoint));
	return isContained;
};

const isPointInGeometry = (
	conditionByScope: ConditionByScope,
	fieldValues: ContainsFieldValue[] | undefined,
	testPoint: EvaluationPoint
): boolean => {
	const typeCondition = conditionByScope[CONTAINS_SCOPES.Type];
	const geometryTypeCode = typeCondition?.Value;
	if(typeof geometryTypeCode !== "string" || geometryTypeCode.trim() === "") {
		throw new Error("Contains evaluation of an entity field requires one scalar TYPE condition.");
	}

	const isContained = (fieldValues ?? [])
		.reduce<Geometry[]>((geometries, value) => {
			return (value.Geometries ?? []).reduce<Geometry[]>((matched, geometry) => {
				if(geometry?.Type?.Code !== geometryTypeCode) {
					return matched;
				}

				const definition = geometry.Definition as GeoJSON;
				const entityGeometries = readGeometries(definition);
				return matched.concat(entityGeometries);
			}, geometries);
		}, [])
		.some(geometry => containsGeometry(geometry, testPoint));
	return isContained;
};

const evaluateContainsForValues = (
	fieldValuesById: FieldValuesById,
	metadata: ContainsMetadata | undefined,
	angularServices?: any
): boolean => {
	const conditions = metadata?.rule?.Conditions ?? [];
	const conditionByScope = conditions.reduce<ConditionByScope>((acc, condition) => {
		if(condition?.Scope?.Code) {
			acc[condition.Scope.Code] = condition;
		}

		return acc;
	}, {});

	const testPoint = tryGetCoordinates(conditionByScope, fieldValuesById);
	if(!testPoint) {
		return false;
	}

	const evaluationSetCondition = conditionByScope[CONTAINS_SCOPES.EvaluationSet];
	const evaluationSetFieldId = evaluationSetCondition?.Field?.Id;
	if(evaluationSetFieldId === undefined) {
		return false;
	}

	const evaluationSetValues = fieldValuesById[evaluationSetFieldId];
	if(!evaluationSetValues?.length) {
		return false;
	}

	const isEntity = evaluationSetValues.some(value => !isValueNullish(value?.EntityId));
	const isShape = evaluationSetValues.some(value => !isValueNullish(value?.Shape?.GeoJson));
	const isGeometry = evaluationSetValues.some(value => (value.Geometries ?? []).length > 0);
	if(isEntity && !isGeometry) {
		const ex = new Error("Contains evaluation of an entity field requires at least one geometry in the EVALUATIONSET field.");
		angularServices?.$logger.exception(ex, "Invalid contains evaluation configuration");

		return false;
	}
	if(!isEntity && !isShape) {
		const ex = new Error("Contains evaluation of a non-entity field requires at least one shape in the EVALUATIONSET field.");
		angularServices?.$logger.exception(ex, "Invalid contains evaluation configuration");
		
		return false;
	}

	const isContained = isEntity
		? isPointInGeometry(conditionByScope, evaluationSetValues, testPoint)
		: isPointInShape(evaluationSetValues, testPoint, angularServices);
	return isContained;
};

export const evaluateContains = (
	valuesByContextId: ValuesByContextId | undefined,
	_compareTo: unknown,
	metadata: ContainsMetadata | undefined,
	angularServices?: any
): boolean => {
	const groups = Object.entries(valuesByContextId ?? {});
	if(groups.length === 0) {
		return false;
	}

	const detailGroups = groups.filter(([detailId]) => detailId !== "undefined");
	const evaluationGroups = detailGroups.length > 0 ? detailGroups : groups;

	const areAllGroupsContained = evaluationGroups.every(([, fieldValuesById]) => evaluateContainsForValues(fieldValuesById, metadata, angularServices));
	return areAllGroupsContained;
};


